/* eslint-disable no-unused-vars */
import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import {
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    Description as DescriptionIcon,
    Gavel as GavelIcon,
} from '@mui/icons-material';
import { prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import { userSetCurrentProcuration } from 'src/Redux/Actions/UserActions';
import Teachr from 'src/Objects/models/Teachr';
import Sponsorship from 'src/Objects/models/Sponsorship';
import { putSponsorship } from 'src/Redux/Actions/SponsorshipActions';
import { store } from 'src/Redux/store';
import { PutSponsorship } from 'src/Objects/interfaces/AppInterface';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import { useProfilContext } from 'src/Contexts/ProfilContexte';
import ButtonField from 'src/Components/Ui/ButtonField';
import Settings from 'src/Components/Settings';
import OptionCard from 'src/Components/Ui/OptionCard';

// Carte d'option cliquable

const useProcurationSAP = (teachr: Teachr, nextStep: (step: number) => void) => {
    const dispatch = useDispatch();
    const [isProcessingProcuration, setIsProcessingProcuration] = useState(false);

    const handleNavigate = useCallback(async (procuration: string) => {
        if (!teachr) return;
        try {
            const updatedTeachr = prepareTeachrForUpdate(teachr, { businessType: teachr?.businessType, procuration: procuration });
            await updateTeachrData(updatedTeachr, dispatch);
            dispatch(userSetCurrentProcuration(parseInt(procuration)));
            nextStep(2);
        } catch (e) {
            console.error('Erreur lors de la mise à jour teachr.procuration:', e);
        }
    }, [teachr, dispatch, nextStep]);

    const handleNavigateToProcurationSap = useCallback(async () => { await handleNavigate('20'); }, [handleNavigate]);
    const handleHasDeclaration = useCallback(async () => { await handleNavigate('40'); }, [handleNavigate]);

    const handleProcurationSAP = useCallback(() => {
        if (teachr?.sponsorship) {
            setIsProcessingProcuration(true);
            const sponsorship = new Sponsorship(teachr.sponsorship);
            sponsorship.statut = 'PROC_SAP';
            const data: PutSponsorship = {
                teachr,
                sponsorship,
                setLoad: setIsProcessingProcuration,
                handleNext: handleNavigateToProcurationSap
            };
            store.dispatch(putSponsorship(data));
        } else {
            handleNavigateToProcurationSap();
        }
    }, [teachr, handleNavigateToProcurationSap]);

    const handlePrevious = useCallback(() => {
        nextStep(0);
    }, [nextStep]);




    return { isProcessingProcuration, handleProcurationSAP, handlePrevious, handleHasDeclaration };
};



const SecondCompletProfil = () => {
    const { brandConfig } = useTailwindBrand();
    const { nextStep, teachr } = useProfilContext();
    const { isProcessingProcuration, handleProcurationSAP, handlePrevious, handleHasDeclaration } = useProcurationSAP(teachr, nextStep);
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';



    return (
        <Settings
            title="Déclaration NOVA"
            subtitle="Service de démarches en ligne des services à la personne"
            icon={<AssignmentIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            <Grid item xs={12}>
                <div className="flex flex-col gap-y-5">
                    {/* Bloc d'explication */}
                    <Box
                        sx={{
                            p: 2.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: `${primaryHex}40`,
                            bgcolor: `${primaryHex}0d`,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                        }}
                    >
                        <CheckCircleIcon sx={{ color: primaryHex, fontSize: 22, flexShrink: 0, mt: 0.25 }} />
                        <Box>
                            <Typography variant="subtitle2" fontWeight="700" sx={{ color: primaryHex, fontFamily: brandConfig.fontFamily, mb: 0.5 }}>
                                Pourquoi cette déclaration ?
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: brandConfig.fontFamily }}>
                                La déclaration NOVA auprès de la DREETS est obligatoire pour que vos clients puissent
                                bénéficier des avantages fiscaux liés aux services à la personne (déduction ou crédit d'impôt
                                de 50 %). Deux options s'offrent à vous ci-dessous.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Cartes d'options */}
                    <OptionCard
                        icon={<GavelIcon />}
                        title="Déléguer la procuration SAP"
                        description="Confiez-nous la démarche. Nous effectuons votre déclaration NOVA auprès de la DREETS à votre place, rapidement et sans tracas."
                        actionLabel="Faire la procuration SAP"
                        onClick={handleProcurationSAP}
                        disabled={isProcessingProcuration}
                        variant="primary"
                        primaryHex={primaryHex}
                        fontFamily={brandConfig.fontFamily}
                    />

                    <OptionCard
                        icon={<DescriptionIcon />}
                        title="J'ai déjà ma déclaration SAP"
                        description="Vous avez déjà effectué votre déclaration NOVA ? Ajoutez-la directement et accédez aux conditions générales pour finaliser votre profil."
                        actionLabel="Ajouter ma déclaration SAP"
                        onClick={handleHasDeclaration}
                        disabled={isProcessingProcuration}
                        variant="secondary"
                        primaryHex={primaryHex}
                        fontFamily={brandConfig.fontFamily}
                    />

                    {/* Bouton retour */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', pt: 1 }}>
                        <ButtonField
                            label="Précédent"
                            variant="outlined"
                            className="font-bold"
                            onClick={handlePrevious}
                        />
                    </Box>
                </div>
            </Grid>
        </Settings>
    );
};

export default SecondCompletProfil;
